import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import {
  initLiff,
  getLineProfile,
  loginWithLine,
  subscribeToLoginToken
} from '../lib/lineAuth';
import { QRCodeCanvas } from 'qrcode.react';
import ActivationModal from '../components/auth/ActivationModal';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';
import MobileQrConfirmCard from '../components/auth/MobileQrConfirmCard';
import StaffActivationView from '../components/auth/StaffActivationView';
import MobileBindingConfirmCard from '../components/auth/MobileBindingConfirmCard';

// Feature detection: Check if device is mobile
export const isMobileDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.innerWidth <= 768 && 'ontouchstart' in window)
  );
};

// Clean mobile whitespace / unicode
export const cleanMobileInput = (str) => {
  if (!str) return '';
  return str
    .replace(/[\u200B\u200C\u200D\uFEFF]/g, '')
    .replace(/^[\s\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+|[\s\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+$/g, '');
};

// Check if current domain, pathname (/risk), or URL param is Risk subdomain/portal
export const isRiskSubdomain = () => {
  if (typeof window === 'undefined') return false;
  const host = (window.location.hostname || '').toLowerCase();
  const pathname = (window.location.pathname || '').toLowerCase();
  const search = window.location.search || '';
  const params = new URLSearchParams(search);
  return (
    host === 'risk.fangdent.com' ||
    host.startsWith('risk.') ||
    params.get('portal') === 'risk' ||
    pathname === '/risk' ||
    pathname.startsWith('/risk/')
  );
};

const LoginPage = ({ onLoginSuccess }) => {
  // Navigation & URL detection
  const [isMobile, setIsMobile] = useState(isMobileDevice);
  const [urlParams, setUrlParams] = useState({ action: null, token: null, userId: null });

  // Synchronous draft check before first render (survives 2-stage LIFF redirects)
  const _initDraft = (() => {
    try {
      const raw =
        sessionStorage.getItem('dental_activation_draft') ||
        localStorage.getItem('dental_activation_draft');
      if (raw) {
        const d = JSON.parse(raw);
        if (d?.activatingUser) return d;
      }
    } catch (e) {}
    return null;
  })();

  const [activationDraft, setActivationDraft] = useState(_initDraft);
  const [activatingUser, setActivatingUser] = useState(_initDraft?.activatingUser || null);
  const [viewMode, setViewMode] = useState(_initDraft?.activatingUser ? 'activation' : 'login');

  // Credentials State
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingLineProfile, setPendingLineProfile] = useState(null);

  // Modals
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showActivationModal, setShowActivationModal] = useState(false);

  // Desktop LINE QR Code State
  const [qrToken, setQrToken] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [qrSecondsLeft, setQrSecondsLeft] = useState(600); // 10 minutes
  const [qrStatus, setQrStatus] = useState('pending'); // 'pending' | 'scanned' | 'approved'
  const unsubscribeQrRef = useRef(null);

  const isDevStaging =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'dev.fangdent.com' || process.env.REACT_APP_ENV === 'staging');

  const isRisk = isRiskSubdomain();

  useEffect(() => {
    if (isRisk && typeof document !== 'undefined') {
      document.title = 'ระบบบริหารความเสี่ยงทันตกรรม (Dental Risk Portal) - เข้าสู่ระบบสำหรับเจ้าหน้าที่';
    }
  }, [isRisk]);

  // Detect URL search params on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const action = searchParams.get('action');
      const token = searchParams.get('token');
      const userId = searchParams.get('userId');
      setUrlParams({ action, token, userId });

      const handleResize = () => setIsMobile(isMobileDevice());
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Handle successful login completion
  const handleSuccess = useCallback(
    (session) => {
      if (unsubscribeQrRef.current) {
        unsubscribeQrRef.current();
      }
      if (onLoginSuccess) {
        onLoginSuccess(session);
      }
    },
    [onLoginSuccess]
  );

  // Check initial LINE profile on mount (One-tap / auto-login)
  useEffect(() => {
    const checkLine = async () => {
      // Don't auto-login if verifying a QR link or in activation flow
      if (
        urlParams.action === 'qr-login' ||
        urlParams.action === 'bind-line' ||
        urlParams.action === 'activation-bind-callback' ||
        viewMode === 'activation' ||
        activationDraft !== null
      ) {
        return;
      }

      try {
        await initLiff();
        const profile = await getLineProfile();
        if (profile && profile.userId) {
          setPendingLineProfile(profile);

          if (supabase) {
            const { data, error } = await supabase.rpc('get_user_by_line_id', {
              p_line_id: profile.userId
            });

            if (!error && Array.isArray(data) && data.length > 0) {
              const staff = data[0];
              const session = {
                user: {
                  id: staff.id,
                  email: staff.email,
                  name: staff.name,
                  role: staff.role,
                  line_user_id: profile.userId,
                  avatar_url: profile.pictureUrl
                }
              };
              localStorage.setItem('dental_line_session', JSON.stringify(session));
              handleSuccess(session);
            }
          }
        }
      } catch (err) {
        console.warn('LINE check exception:', err);
      }
    };

    checkLine();
  }, [urlParams.action, handleSuccess, viewMode, activationDraft]);

  // Generate / Refresh Desktop QR Code Token
  const generateQrToken = useCallback(async () => {
    if (!supabase) return;
    setQrLoading(true);
    setQrStatus('pending');
    setQrSecondsLeft(600);

    if (unsubscribeQrRef.current) {
      unsubscribeQrRef.current();
      unsubscribeQrRef.current = null;
    }

    try {
      const { data: token, error } = await supabase.rpc('create_login_token');
      if (error) {
        console.error('Failed to create login token:', error);
        return;
      }

      setQrToken(token);

      // Subscribe to Realtime and Fallback Polling
      const cleanup = subscribeToLoginToken(token, async (payload) => {
        setQrStatus('approved');
        let staffUser = payload?.user;

        if (!staffUser && payload?.line_user_id) {
          try {
            const { data } = await supabase.rpc('get_user_by_line_id', {
              p_line_id: payload.line_user_id
            });
            if (data && data.length > 0) staffUser = data[0];
          } catch (e) {}
        }

        const session = {
          user: {
            id: staffUser?.id || payload?.user_id,
            email: staffUser?.email || payload?.user_email || 'staff@fangdent.com',
            name: staffUser?.name || payload?.user_name || 'LINE User',
            role: staffUser?.role || payload?.user_role || 'staff',
            line_user_id: payload?.line_user_id,
            avatar_url: payload?.line_picture_url || staffUser?.avatar_url
          }
        };

        localStorage.setItem('dental_line_session', JSON.stringify(session));
        handleSuccess(session);
      });

      unsubscribeQrRef.current = cleanup;
    } catch (err) {
      console.error('Error generating QR token:', err);
    } finally {
      setQrLoading(false);
    }
  }, [handleSuccess]);

  // Desktop: Auto-generate QR code on mount
  useEffect(() => {
    if (!isMobile && !urlParams.action) {
      generateQrToken();
    }
    return () => {
      if (unsubscribeQrRef.current) {
        unsubscribeQrRef.current();
      }
    };
  }, [isMobile, urlParams.action, generateQrToken]);

  // Countdown timer for QR code validity
  useEffect(() => {
    if (!qrToken || qrSecondsLeft <= 0) return;
    const timer = setInterval(() => {
      setQrSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [qrToken, qrSecondsLeft]);

  // Quick Dev Login for Synology NAS Dev Staging Environment
  const handleDevQuickLogin = async (targetRole) => {
    setLoading(true);
    setErrorMessage('');
    try {
      let profile = null;
      if (supabase) {
        const { data } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('role', targetRole)
          .limit(1);
        if (data && data.length > 0) {
          profile = data[0];
        }
      }

      if (!profile) {
        const roleEmails = {
          admin: 'dev.admin@fangdent.com',
          'ทันตแพทย์': 'dev.dentist@fangdent.com',
          'ผู้ช่วยทันตแพทย์': 'dev.staff@fangdent.com'
        };
        const roleNames = {
          admin: 'แอดมิน ทดสอบระบบ (Dev Admin)',
          'ทันตแพทย์': 'ทพ. ทดสอบระบบ (Dentist)',
          'ผู้ช่วยทันตแพทย์': 'ผู้ช่วย ทดสอบระบบ (Assistant)'
        };
        profile = {
          id: `dev-user-${targetRole}`,
          email: roleEmails[targetRole] || `dev.${targetRole}@fangdent.com`,
          name: roleNames[targetRole] || `ผู้ใช้ทดสอบ (${targetRole})`,
          role: targetRole,
          allowed_pages: 'consumable,durable,handpiece,packaging,washing,supply,dispense,clinic-order,barcode-tracking,trace,recall,dental-risk',
          is_activated: true
        };
      }

      const devSession = {
        access_token: 'dev-token-' + Date.now(),
        user: {
          id: profile.id,
          email: profile.email,
          name: profile.name,
          role: profile.role,
          allowed_pages: profile.allowed_pages,
          avatar_url: profile.avatar_url || null
        }
      };
      handleSuccess(devSession);
    } catch (err) {
      console.error('Dev quick login error:', err);
      setErrorMessage('เกิดข้อผิดพลาดในการเข้าสู่ระบบทดสอบ');
    } finally {
      setLoading(false);
    }
  };

  // Handle Username/Password Login
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    const cleanUser = cleanMobileInput(username);
    const cleanPass = cleanMobileInput(password);

    if (!cleanUser || !cleanPass) {
      setErrorMessage('กรุณากรอกชื่อผู้ใช้งานและรหัสผ่าน');
      setLoading(false);
      return;
    }

    let resolvedEmail = cleanUser;

    // 1. Primary lookup mechanism: get_email_by_username RPC
    try {
      if (supabase) {
        const { data: rpcEmail, error: rpcErr } = await supabase.rpc('get_email_by_username', {
          p_username: cleanUser
        });

        if (!rpcErr && rpcEmail) {
          resolvedEmail = rpcEmail;
        } else if (!cleanUser.includes('@')) {
          if (cleanUser.toLowerCase() === 'nuntsak') {
            resolvedEmail = 'nuntsak@gmail.com';
          } else {
            resolvedEmail = `${cleanUser}@fangdent.com`;
          }
        }
      }
    } catch (lookupErr) {
      console.warn('Username RPC lookup error:', lookupErr);
      if (!cleanUser.includes('@')) {
        resolvedEmail = `${cleanUser}@fangdent.com`;
      }
    }

    try {
      if (!supabase) {
        throw new Error('Supabase is not initialized');
      }

      // 2. Authenticate with resolved email and password
      const { data, error } = await supabase.auth.signInWithPassword({
        email: resolvedEmail,
        password: cleanPass
      });

      if (error) {
        if (isDevStaging) {
          // Dev Staging: Authenticate against local PostgreSQL on Synology NAS
          try {
            const { data: localProfiles } = await supabase
              .from('user_profiles')
              .select('*');

            const cleanInput = cleanUser.toLowerCase().trim();
            const matched = localProfiles?.find((p) =>
              (p.email && p.email.toLowerCase() === resolvedEmail.toLowerCase()) ||
              (p.email && p.email.toLowerCase().includes(cleanInput)) ||
              (p.name && p.name.toLowerCase().includes(cleanInput))
            );

            if (matched) {
              const devSession = {
                access_token: 'dev-token-' + Date.now(),
                user: {
                  id: matched.id,
                  email: matched.email,
                  name: matched.name,
                  role: matched.role,
                  allowed_pages: matched.allowed_pages,
                  avatar_url: matched.avatar_url || null
                }
              };
              handleSuccess(devSession);
              setLoading(false);
              return;
            }
          } catch (devAuthErr) {
            console.warn('Local dev profile auth error:', devAuthErr);
          }
        }

        // 1. Check for first-time staff activation
        const { data: firstTime, error: rpcErr } = await supabase.rpc('check_first_time_login', {
          p_first_name: cleanUser,
          p_last_name: cleanPass
        });

        if (!rpcErr && Array.isArray(firstTime) && firstTime.length > 0) {
          const user = firstTime[0];
          if (user.is_activated === false) {
            setActivatingUser(user);
            setViewMode('activation');
            setLoading(false);
            return;
          }
        }

        // 2. Check if user already activated
        const { data: actUser } = await supabase.rpc('check_activated_user', {
          p_first_name: cleanUser,
          p_last_name: cleanPass
        });

        if (actUser && actUser.length > 0) {
          setErrorMessage(
            `คุณ ${cleanUser} ${cleanPass} ได้ทำการเปิดใช้งานบัญชีแล้ว กรุณาเข้าสู่ระบบด้วยอีเมลจริง (${actUser[0].email}) และรหัสผ่านที่ท่านได้ตั้งไว้`
          );
        } else {
          setErrorMessage('ชื่อผู้ใช้งานหรือรหัสผ่านไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง');
        }
        setLoading(false);
        return;
      }

      // 3. Strict staff verification after successful authentication
      const session = data?.session;
      if (!session || !session.user) {
        setErrorMessage('ไม่พบข้อมูลเซสชันการเข้าสู่ระบบ');
        setLoading(false);
        return;
      }

      let { data: profile, error: profileErr } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle();

      if (!profile && session.user.email) {
        const { data: profileByEmail } = await supabase
          .from('user_profiles')
          .select('*')
          .ilike('email', session.user.email)
          .maybeSingle();
        if (profileByEmail) {
          profile = profileByEmail;
          profileErr = null;
        }
      }

      const validStaffRoles = ['admin', 'ทันตแพทย์', 'ทันตาภิบาล', 'ผู้ช่วยทันตแพทย์', 'เจ้าหน้าที่', 'staff', 'clinic', 'อื่นๆ'];
      const isStaffRole = profile?.role && validStaffRoles.includes(profile.role);
      const isStaffActive = profile && profile.is_activated !== false && isStaffRole;

      if (profileErr || !profile || !isStaffActive) {
        await supabase.auth.signOut();
        try {
          localStorage.removeItem('dental_line_session');
        } catch (e) {}
        setErrorMessage('ขออภัย บัญชีนี้ไม่ได้รับอนุญาตให้เข้าใช้งาน (เฉพาะเจ้าหน้าที่ที่ลงทะเบียนแล้วเท่านั้น)');
        setLoading(false);
        return;
      }

      // Link LINE account if user had pending LINE profile
      if (pendingLineProfile?.userId) {
        try {
          await supabase
            .from('user_profiles')
            .update({
              line_user_id: pendingLineProfile.userId,
              line_display_name: pendingLineProfile.displayName,
              line_picture_url: pendingLineProfile.pictureUrl
            })
            .eq('id', profile.id || session.user.id);

          localStorage.setItem(
            'dental_line_session',
            JSON.stringify({
              user: {
                ...session.user,
                name: profile.name || session.user.user_metadata?.full_name || session.user.name,
                role: profile.role,
                line_user_id: pendingLineProfile.userId,
                avatar_url: pendingLineProfile.pictureUrl
              }
            })
          );
        } catch (e) {
          console.warn('LINE binding on login warning:', e);
        }
      }

      const enrichedSession = {
        ...session,
        user: {
          ...session.user,
          id: profile.id || session.user.id,
          name: profile.name || session.user.user_metadata?.full_name || session.user.name || session.user.email,
          role: profile.role,
          allowed_pages: profile.allowed_pages,
          avatar_url: profile.avatar_url || profile.line_picture_url || session.user.user_metadata?.avatar_url
        }
      };

      handleSuccess(enrichedSession);
    } catch (err) {
      console.error('Password login exception:', err);
      setErrorMessage(err.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ');
    } finally {
      setLoading(false);
    }
  };

  // Case 1: Mobile opened via LINE binding link (?action=bind-line&token=...&userId=...)
  if (urlParams.action === 'bind-line' && urlParams.token) {
    return (
      <MobileBindingConfirmCard
        token={urlParams.token}
        userId={urlParams.userId}
        onReturnHome={() => {
          window.location.href = window.location.origin;
        }}
      />
    );
  }

  // Case 2: Mobile opened via QR scan link (?action=qr-login&token=...)
  if (urlParams.action === 'qr-login' || (urlParams.token && isMobile)) {
    return (
      <MobileQrConfirmCard
        token={urlParams.token}
        onReturnHome={() => {
          window.location.href = window.location.origin;
        }}
      />
    );
  }

  // Case 3: First-time Staff Activation in-page view
  if (viewMode === 'activation' && activatingUser) {
    return (
      <StaffActivationView
        activatingUser={activatingUser}
        savedDraft={activationDraft}
        onBackToLogin={() => {
          try {
            sessionStorage.removeItem('dental_activation_draft');
            localStorage.removeItem('dental_activation_draft');
          } catch (e) {}
          setActivationDraft(null);
          setViewMode('login');
        }}
        onActivationSuccess={(uname) => {
          try {
            sessionStorage.removeItem('dental_activation_draft');
            localStorage.removeItem('dental_activation_draft');
          } catch (e) {}
          setActivationDraft(null);
          setUsername(uname);
          setPassword('');
          setViewMode('login');
        }}
      />
    );
  }

  const qrUrl =
    typeof window !== 'undefined' && qrToken
      ? `${window.location.origin}/?action=qr-login&token=${qrToken}`
      : '';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        boxSizing: 'border-box'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: isMobile ? '440px' : '960px',
          backgroundColor: '#ffffff',
          borderRadius: '28px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row'
        }}
      >
        {/* ========================================================= */}
        {/* LEFT COLUMN: Username & Password Login Form               */}
        {/* ========================================================= */}
        <div
          style={{
            flex: '1 1 50%',
            padding: isMobile ? '32px 24px' : '44px 40px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}
        >
          {isDevStaging && (
            <div
              data-testid="dev-staging-badge"
              style={{
                marginBottom: '18px',
                padding: '12px 14px',
                backgroundColor: '#fff7ed',
                border: '1.5px solid #f97316',
                borderRadius: '14px',
                color: '#c2410c',
                fontSize: '0.85em',
                textAlign: 'left'
              }}
            >
              <div style={{ fontWeight: '800', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>🧪</span>
                <span>ระบบทดสอบ Synology NAS (PostgreSQL)</span>
              </div>
              <div style={{ fontSize: '0.82em', color: '#9a3412', marginBottom: '8px' }}>
                คลิกเลือกบทบาทเพื่อเข้าทดสอบได้ทันที:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                <button
                  type="button"
                  data-testid="btn-dev-login-admin"
                  onClick={() => handleDevQuickLogin('admin')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#ea580c',
                    color: '#ffffff',
                    border: 'none',
                    fontSize: '0.82em',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  👑 แอดมิน (Admin)
                </button>
                <button
                  type="button"
                  data-testid="btn-dev-login-dentist"
                  onClick={() => handleDevQuickLogin('ทันตแพทย์')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#0284c7',
                    color: '#ffffff',
                    border: 'none',
                    fontSize: '0.82em',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  🩺 ทันตแพทย์
                </button>
                <button
                  type="button"
                  data-testid="btn-dev-login-staff"
                  onClick={() => handleDevQuickLogin('ผู้ช่วยทันตแพทย์')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#16a34a',
                    color: '#ffffff',
                    border: 'none',
                    fontSize: '0.82em',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  🧼 จ่ายกลาง (CSSD)
                </button>
              </div>
            </div>
          )}

          {/* Brand Header */}
          <div style={{ marginBottom: '28px', textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '14px',
                  backgroundColor: isRisk ? '#fef2f2' : '#eff6ff',
                  color: isRisk ? '#ef4444' : '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}
              >
                {isRisk ? '🛡️' : '🏥'}
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: '1.35em', fontWeight: '800', color: '#0f172a' }}>
                  {isRisk ? 'ระบบบริหารความเสี่ยงทันตกรรม (Dental Risk Portal)' : 'ระบบ CSSD'}
                </h1>
                <p style={{ margin: 0, fontSize: '0.82em', color: '#64748b', fontWeight: '500' }}>
                  {isRisk ? 'เข้าสู่ระบบสำหรับเจ้าหน้าที่' : 'โรงพยาบาลทันตกรรม'}
                </p>
              </div>
            </div>
            <p style={{ margin: 0, fontSize: '0.9em', color: '#64748b' }}>
              {isRisk
                ? 'ระบบบริหารความเสี่ยงทันตกรรม (Dental Risk Portal) - เข้าสู่ระบบสำหรับเจ้าหน้าที่'
                : 'กรุณาเข้าสู่ระบบเพื่อเข้าใช้งานระบบบริหารจัดการ'}
            </p>
          </div>

          {/* First-Time Activation Banner */}
          <div
            style={{
              padding: '12px 14px',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '14px',
              marginBottom: '20px',
              fontSize: '0.82em',
              color: '#1e40af',
              lineHeight: 1.45,
              textAlign: 'left'
            }}
          >
            💡 <strong>เข้าใช้งานครั้งแรก?</strong> กรอกชื่อเป็น Username และนามสกุลเป็น Password
            เพื่อเริ่มต้นเปิดใช้งานบัญชี
          </div>

          {/* Pending LINE Profile Notice */}
          {pendingLineProfile && (
            <div
              style={{
                padding: '12px 14px',
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '14px',
                marginBottom: '18px',
                fontSize: '0.85em',
                color: '#166534',
                textAlign: 'left'
              }}
            >
              พบบัญชี LINE: <strong>{pendingLineProfile.displayName}</strong>{' '}
              (ยังไม่ได้ผูกกับบัญชีเจ้าหน้าที่ กรุณากรอกรหัสผ่านเพื่อเชื่อมต่อ)
            </div>
          )}

          {errorMessage && (
            <div
              style={{
                padding: '12px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '12px',
                color: '#b91c1c',
                fontSize: '0.85em',
                marginBottom: '18px',
                lineHeight: 1.45,
                textAlign: 'left'
              }}
            >
              ⚠️ {errorMessage}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handlePasswordLogin} style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.85em',
                  color: '#334155',
                  fontWeight: '600',
                  marginBottom: '6px'
                }}
              >
                ชื่อผู้ใช้งาน หรือ อีเมล
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="ชื่อผู้ใช้งาน หรือ email@example.com"
                required
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '0.95em',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
              />
            </div>

            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <label
                  style={{
                    fontSize: '0.85em',
                    color: '#334155',
                    fontWeight: '600'
                  }}
                >
                  รหัสผ่าน
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotModal(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563eb',
                    fontSize: '0.82em',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  ลืมรหัสผ่าน?
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 42px 12px 14px',
                    borderRadius: '12px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.95em',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#64748b',
                    fontSize: '16px',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: loading ? '#94a3b8' : '#2563eb',
                color: '#ffffff',
                border: 'none',
                borderRadius: '14px',
                fontSize: '1em',
                fontWeight: 'bold',
                cursor: loading ? 'wait' : 'pointer',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
                transition: 'all 0.2s ease'
              }}
            >
              {loading ? '⏳ กำลังตรวจสอบ...' : 'เข้าสู่ระบบ'}
            </button>
          </form>

          {/* Mobile One-Tap LINE Login */}
          {isMobile && (
            <div style={{ marginTop: '24px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  textAlign: 'center',
                  marginBottom: '16px',
                  color: '#94a3b8',
                  fontSize: '0.82em'
                }}
              >
                <div style={{ flex: 1, borderBottom: '1px solid #e2e8f0' }} />
                <span style={{ padding: '0 12px' }}>หรือเข้าสู่ระบบด้วย</span>
                <div style={{ flex: 1, borderBottom: '1px solid #e2e8f0' }} />
              </div>

              <button
                type="button"
                onClick={() => loginWithLine({ redirectUri: window.location.origin })}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#06C755',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '1em',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  boxShadow: '0 4px 14px rgba(6, 199, 85, 0.3)'
                }}
              >
                <span>💬</span> เข้าสู่ระบบด้วย LINE
              </button>
            </div>
          )}
        </div>

        {/* ========================================================= */}
        {/* RIGHT COLUMN: LINE QR Code Login (Desktop Only)           */}
        {/* ========================================================= */}
        {!isMobile && (
          <div
            style={{
              flex: '1 1 50%',
              backgroundColor: '#f8fafc',
              borderLeft: '1px solid #f1f5f9',
              padding: '44px 36px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 14px',
                borderRadius: '20px',
                backgroundColor: '#dcfce7',
                color: '#15803d',
                fontSize: '0.82em',
                fontWeight: '700',
                marginBottom: '16px'
              }}
            >
              <span>💬</span> LINE Quick Login
            </div>

            <h3 style={{ margin: '0 0 6px 0', fontSize: '1.25em', fontWeight: '800', color: '#0f172a' }}>
              เข้าสู่ระบบด้วย LINE ผ่าน QR Code
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.85em', color: '#64748b', maxWidth: '300px' }}>
              เปิดกล้องมือถือหรือแอป LINE สแกน QR Code นี้เพื่อเข้าสู่ระบบทันที
            </p>

            {/* QR Code Canvas Card */}
            <div
              style={{
                padding: '20px',
                backgroundColor: '#ffffff',
                borderRadius: '22px',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
                border: '2px solid #06C755',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '220px',
                minHeight: '220px'
              }}
            >
              {qrLoading ? (
                <div style={{ color: '#64748b', fontSize: '0.9em' }}>⏳ กำลังสร้าง QR Code...</div>
              ) : qrSecondsLeft <= 0 ? (
                <div style={{ textAlign: 'center', padding: '10px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>⌛</div>
                  <div style={{ fontSize: '0.88em', color: '#dc2626', fontWeight: 'bold' }}>
                    QR Code หมดอายุแล้ว
                  </div>
                </div>
              ) : qrStatus === 'approved' ? (
                <div style={{ textAlign: 'center', padding: '10px' }}>
                  <div style={{ fontSize: '36px', marginBottom: '8px' }}>✅</div>
                  <div style={{ fontSize: '0.95em', color: '#166534', fontWeight: 'bold' }}>
                    ยืนยันตัวตนสำเร็จแล้ว!
                  </div>
                  <div style={{ fontSize: '0.8em', color: '#475569', marginTop: '4px' }}>
                    กำลังเข้าสู่ระบบ...
                  </div>
                </div>
              ) : qrToken ? (
                <a
                  href={qrUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="desktop-qr-canvas-link"
                  style={{ display: 'block', cursor: 'pointer' }}
                >
                  <QRCodeCanvas
                    value={qrUrl}
                    size={200}
                    level="H"
                    bgColor="#ffffff"
                    fgColor="#0f172a"
                  />
                </a>
              ) : null}
            </div>

            {/* Timer & Controls */}
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              {qrSecondsLeft > 0 ? (
                <span style={{ fontSize: '0.82em', color: '#64748b' }}>
                  รหัสจะหมดอายุใน{' '}
                  <strong style={{ color: '#0f172a' }}>
                    {Math.floor(qrSecondsLeft / 60)}:
                    {('0' + (qrSecondsLeft % 60)).slice(-2)}
                  </strong>{' '}
                  นาที
                </span>
              ) : (
                <span style={{ fontSize: '0.82em', color: '#dc2626', fontWeight: '600' }}>
                  หมดเวลาการใช้งาน
                </span>
              )}

              <button
                type="button"
                onClick={generateQrToken}
                disabled={qrLoading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#06C755',
                  fontSize: '0.82em',
                  fontWeight: '700',
                  cursor: qrLoading ? 'wait' : 'pointer',
                  padding: '4px 8px'
                }}
              >
                🔄 รีเฟรช QR
              </button>
            </div>

            <div
              style={{
                marginTop: '16px',
                fontSize: '0.75em',
                color: '#94a3b8',
                maxWidth: '280px',
                lineHeight: 1.4
              }}
            >
              ถ้าอยู่ในมือถือหรือไม่สามารถสแกนได้ ให้กดที่รูป QR Code
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <ForgotPasswordModal
        isOpen={showForgotModal}
        onClose={() => setShowForgotModal(false)}
      />

      <ActivationModal
        isOpen={showActivationModal}
        activatingUser={activatingUser}
        lineProfile={pendingLineProfile}
        onClose={() => setShowActivationModal(false)}
        onActivationSuccess={handleSuccess}
      />
    </div>
  );
};

export default LoginPage;
